import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import axios from 'axios';
import { PvgisWeatherResponse } from '@shared/models/pvgis-weather.model';
import { PvgisStoreState, PvgisWeatherData } from './pvgis-state.model';
import { getHmrStoreState, initHmrStore } from '../../helpers/vite.helpers';
import { coordinatesEqual } from '@shared/helpers/geometry/coordinates.helpers';
import { Coordinate } from '@shared/models/geography.model';
import { enrichWeatherHours } from '@shared/helpers/energy/weather.helpers';

const STORE_NAME = 'pvgis';
let latestFetchGeneration = 0;

/**
 * Store for PVGIS weather data. Avoid using this store directly,
 * instead use the hooks in pvgis.hook.ts
 */
export const usePvgisStore = createWithEqualityFn<PvgisStoreState>()(
  devtools(
    (set, get) => ({
      weatherData: undefined,
      currentCoordinates: undefined,
      isLoading: false,
      error: undefined,
      currentPromise: undefined,
      ...getHmrStoreState(STORE_NAME),

      fetchPvgisData: async (coordinates: Coordinate) => {
        const { currentCoordinates, weatherData, currentPromise } = get();

        const COORDINATE_EQUALITY_PRECISION_METERS = 500; // we should probably lower this if we can move the fetching to not block UI rendering in DataLoaderProxy
        if (
          coordinatesEqual(
            currentCoordinates,
            coordinates,
            COORDINATE_EQUALITY_PRECISION_METERS,
          )
        ) {
          if (weatherData) return weatherData.rawData; // Return locally cached raw response for these coordinates
          if (currentPromise) return currentPromise; // Return existing promise if request is already in flight for same coordinates
        }

        // Create new request promise
        const fetchGeneration = ++latestFetchGeneration;
        const fetchPromise = (async (): Promise<PvgisWeatherResponse> => {
          try {
            const { data } = await axios.get<PvgisWeatherResponse>(
              `/proxy/pvgis`,
              { params: coordinates },
            );

            // Process the raw data into EnvironmentHourData
            const environmentHourData = enrichWeatherHours(
              data.data.outputs.tmy_hourly,
              coordinates,
            );

            const processedData: PvgisWeatherData = {
              rawData: data,
              environmentHourData,
            };

            // A newer coordinate request may have finished first. Keep its
            // weather data instead of letting this stale response move the
            // cache back to an older pin position.
            if (fetchGeneration === latestFetchGeneration) {
              set(() => ({
                weatherData: processedData,
                currentCoordinates: coordinates,
                isLoading: false,
                error: undefined,
                currentPromise: undefined,
              }));
            }

            return data;
          } catch (err: any) {
            const error = err instanceof Error ? err : new Error(String(err));

            if (fetchGeneration === latestFetchGeneration) {
              set(() => ({
                isLoading: false,
                error,
                currentPromise: undefined,
              }));
            }

            throw error;
          }
        })();

        // Store promise and update loading state
        set(() => ({
          currentCoordinates: coordinates,
          isLoading: true,
          error: undefined,
          currentPromise: fetchPromise,
        }));

        return fetchPromise;
      },

      clearPvgisData: () => {
        latestFetchGeneration += 1;
        set(() => ({
          weatherData: undefined,
          currentCoordinates: undefined,
          isLoading: false,
          error: undefined,
          currentPromise: undefined,
        }));
      },
    }),
    { name: STORE_NAME },
  ),
  shallow,
);

initHmrStore(STORE_NAME, usePvgisStore);
